import { motion } from "framer-motion";
import { Check, Minus } from "lucide-react";
import { useApp } from "@/context/AppProviders";

const Cell = ({ on, isNew }) =>
  on ? (
    <Check
      className={`mx-auto h-4 w-4 ${isNew ? "text-emerald-400" : "text-blue-500"}`}
    />
  ) : (
    <Minus className="mx-auto h-4 w-4 text-muted-foreground/40" />
  );

const LicenseMatrix = () => {
  const { t } = useApp();
  const m = t.matrix;

  return (
    <section
      id="matrix"
      data-testid="matrix-section"
      className="py-24 sm:py-32 border-t border-border"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.5 }}
          className="max-w-2xl"
        >
          <p className="font-mono text-sm uppercase tracking-widest text-blue-500">
            {m.label}
          </p>
          <h2 className="font-heading mt-3 text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight">
            {m.title}
          </h2>
          <p className="mt-4 text-base leading-relaxed text-muted-foreground">
            {m.subtitle}
          </p>
        </motion.div>

        {/* Comparison table */}
        <div className="mt-12 overflow-x-auto rounded-sm border border-border">
          <table className="w-full min-w-[640px] border-collapse text-sm" data-testid="matrix-table">
            <thead>
              <tr className="bg-card">
                <th className="px-5 py-4 text-left font-heading font-semibold">
                  {m.tierLabels.feature}
                </th>
                <th className="px-4 py-4 text-center font-heading font-semibold">
                  {m.tierLabels.standard}
                </th>
                <th className="px-4 py-4 text-center font-heading font-semibold text-blue-500">
                  {m.tierLabels.professional}
                </th>
                <th className="px-4 py-4 text-center font-heading font-semibold">
                  {m.tierLabels.expert}
                </th>
              </tr>
            </thead>
            <tbody>
              {m.rows.map((row, i) => (
                <tr
                  key={i}
                  data-testid={`matrix-row-${i}`}
                  className="border-t border-border odd:bg-card/40"
                >
                  <td className="px-5 py-3 text-muted-foreground">
                    <span className="flex items-center gap-2">
                      {row.feature}
                      {row.isNew && (
                        <span className="rounded-sm bg-emerald-500/15 px-1.5 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-wide text-emerald-400">
                          {m.newTag}
                        </span>
                      )}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <Cell on={row.s} isNew={row.isNew} />
                  </td>
                  <td className="px-4 py-3 bg-blue-600/[0.04]">
                    <Cell on={row.p} isNew={row.isNew} />
                  </td>
                  <td className="px-4 py-3">
                    <Cell on={row.e} isNew={row.isNew} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Who picks which tier */}
        <div className="mt-10">
          <h3 className="font-heading text-lg font-medium tracking-tight">
            {m.whoTitle}
          </h3>
          <div className="mt-4 grid gap-4 md:grid-cols-3">
            {m.who.map((w, i) => (
              <motion.div
                key={w.tier}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className="rounded-sm border border-border bg-card p-5"
              >
                <h4 className="font-heading font-semibold tracking-tight">
                  {w.tier}
                </h4>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                  {w.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default LicenseMatrix;
